import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { NzMessageService } from 'ng-zorro-antd/message';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { ActionService, cleanObject, CustomFormatter, CustomValidators, ISitemap, ISitemapCommand, ISitemapModel, SITEMAP } from 'pbc.angular';
import { toInitials } from 'pbc.functions';

import { IPostProjektArtResponse, IProjektArt } from 'fa-kt.types';
import { ProjektArtsService } from '../../../query';
import { PostProjektArtCommandService } from '../service';

interface IFormValue {
  projektArt?: IProjektArt;
}

@Component({
  selector: 'fa-kt-post-projekt-art',
  templateUrl: './post-projekt-art.component.html',
  styleUrls: ['./post-projekt-art.component.css'],
})
export class PostProjektArtCommandComponent implements OnInit, OnDestroy {
  command!: ISitemapCommand;
  model!: ISitemapModel;

  subscriptions: Subscription[] = [];
  form!: FormGroup;
  $loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  $valid: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  CustomFormatter = CustomFormatter;

  @Input() set loading(loading: boolean) {
    this.$loading.next(this.$loading.getValue() || loading);
  }
  @Input() set value(value: IFormValue) {
    this.patch(value);
  }
  @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() finished: EventEmitter<IPostProjektArtResponse> = new EventEmitter<IPostProjektArtResponse>();

  constructor(
    @Inject(SITEMAP) private sitemap: ISitemap,
    private fb: FormBuilder,
    private actions: ActionService,
    private message: NzMessageService,
    public projektArts: ProjektArtsService,
    public postProjektArt: PostProjektArtCommandService /* << dependencies    */ /*    dependencies >> */,
  ) {
    this.command = sitemap.PROJEKTE.Commands.POST_PROJEKT_ART;
    this.model = sitemap.PROJEKTE.Models.PROJEKT_ART;
    /* << constructor    */
    /*    constructor >> */
    this.form = this.fb.group({
      projektArt: this.fb.group({
        id: [null, []],
        name: [null, [Validators.required]],
        initialen: [null, [Validators.required]],
        beschreibung: [null, []],
        order: [null, []],
      }),
    });
    this.initialen?.setValidators([Validators.required, CustomValidators.isUnique(this.projektArts.initialenIsUnique(this.id))]);
    this.patch();
  }

  ngOnInit() {
    this.subscriptions.push(
      this.form.valueChanges.subscribe((value: any) => {
        this.valid.emit(this.form.valid);
        if (this.form.valid) {
          this.actions.subscribe({ key: this.command.translation, action: async () => await this.submit() });
        } else {
          this.actions.unsubscribe(this.command.translation);
        }
      }),
      this.id?.valueChanges.subscribe(async (id) => {
        /* << on-id-change    */
        /*    on-id-change >> */
      }) as Subscription,
      this.name?.valueChanges.subscribe(async (name) => {
        /* << on-name-change    */
        /*    on-name-change >> */
      }) as Subscription,
      this.initialen?.valueChanges.subscribe(async (initialen) => {
        /* << on-initialen-change    */
        /*    on-initialen-change >> */
      }) as Subscription,
      this.beschreibung?.valueChanges.subscribe(async (beschreibung) => {
        /* << on-beschreibung-change    */
        /*    on-beschreibung-change >> */
      }) as Subscription,
      /* << subscibe    */
      this.form.controls.projektArt
        ?.get('name')
        ?.valueChanges.pipe(debounceTime(200), distinctUntilChanged())
        .subscribe((name: string) => {
          const initialen = this.form.controls.projektArt?.get('initialen');
          if (initialen && (!initialen.touched || !initialen.value)) {
            this.form.patchValue({ projektArt: { initialen: toInitials(name) } });
          }
        }) as Subscription,
      /*    subscibe >> */
    );
    /* << init    */
    /*    init >> */
  }

  ngOnDestroy() {
    this.actions.unsubscribe(this.command.translation);
    this.subscriptions.filter(($: Subscription) => $ && !$.closed).forEach(($: Subscription) => $.unsubscribe());
  }

  private patch(value?: IFormValue) {
    let projektArt = {
      id: null,
      name: null,
      initialen: null,
      beschreibung: null,
      order: 0,
    };
    if (value && value.projektArt) {
      projektArt = {
        ...projektArt,
        ...cleanObject(value.projektArt),
      };
    }
    this.form.patchValue({ projektArt });
    /* << custom-stuff    */
    /*    custom-stuff >> */
    this.update();
  }

  update() {
    this.$loading.next(true);
    this.form.markAsDirty();
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();
    this.$valid.next(this.form.valid);
    this.$loading.next(false);
  }

  async submit() {
    this.update();
    if (!this.form.valid) {
      return;
    }
    this.$loading.next(true);
    try {
      const payload = cleanObject(this.form.getRawValue());
      if (payload && payload.projektArt) {
        payload.projektArt = cleanObject(payload.projektArt);
      }
      const response: IPostProjektArtResponse | undefined = await this.postProjektArt.request(payload);
      this.finished.next(response);
    } catch (error: any) {
      this.message.error(error.message);
      for (const validation of error.errors ? error.errors : []) {
        for (const children of validation.children ? validation.children : []) {
          const message = Object.values(children.constraints).reduce((acc, val) => acc + ' ' + val, '');
          this.form.controls.objektArt.get(children.property)?.setErrors({ server: message });
        }
      }
    }
    this.$loading.next(false);
  }

  get id() {
    return this.form.controls.projektArt.get('id');
  }

  get name() {
    return this.form.controls.projektArt.get('name');
  }

  get initialen() {
    return this.form.controls.projektArt.get('initialen');
  }

  get beschreibung() {
    return this.form.controls.projektArt.get('beschreibung');
  }
}
